import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  Link2,
  ListChecks,
  Lock,
  Settings,
  X,
} from 'lucide-react';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@cotera/client/v0/components/ui/alert';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';
import { ResourceType } from '@cotera/api';
import { RESOURCE_TYPE_CONFIG } from '../type/resource-type-config';
import { resourceItemPath } from '../navigation/resource-navigator';
import {
  canOfferViewFor,
  describeShareTargets,
  shareTargetKey,
  type ShareTarget,
} from './share-targets';
import { ShareConfirmAlert } from './share-confirm-alert';
import {
  permissionLabel,
  SharePermissionSelect,
  type SharePermission,
  toSharePermission,
} from './share-permission-select';
import {
  useShareTargetTitle,
  useApplyShareChange,
  useCombinedAccess,
  type CombinedAccess,
} from './use-share-access';
import { useShareToolsWarning } from './use-share-tools-warning';
import {
  buildShareUrl,
  defaultLinkSettings,
  effectivePermission,
  linkAudienceLabel,
  ShareSettingsView,
  useCopyShareLink,
  type LinkSettings,
} from './share-link-settings';
import { ShareActiveLinks } from './share-active-links';
import { ShareWhatTheyGet } from './share-what-they-get';
import { Invitee, inviteeKey, InvitePeople } from './share-invite-field';
import {
  GeneralAccess,
  GeneralAccessRow,
  PeopleWithAccess,
  Pill,
} from './share-people-list';

export type ShareLayout = 'single' | 'bundle' | 'bulk';

export type SharePanelProps = {
  /**
   * What can be shared. In a `bundle` (a chat and its artifacts) every item is
   * a choice and all start chosen; in `bulk` (a multi-selection from a list)
   * they are shared together as given.
   */
  targets: ShareTarget[];
  layout: ShareLayout;
  /** Closes the modal hosting the panel. */
  onClose: () => void;
};

type PanelView = 'main' | 'settings' | 'links';

/**
 * Sharing in one panel: invite people, see who is in, decide whether the whole
 * org can open it, and copy a link. Everything rarer — who a link works for,
 * whether it hands out copies, expiry, live links — sits one level down under
 * the link settings gear, so the first view stays short.
 */
export const SharePanel: React.FC<SharePanelProps> = (props) => {
  const [first] = props.targets;
  if (first === undefined) {
    return null;
  }
  return <SharePanelContent {...props} first={first} />;
};

const SharePanelContent: React.FC<SharePanelProps & { first: ShareTarget }> = (
  props
) => {
  const [view, setView] = useState<PanelView>('main');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(props.targets.map(shareTargetKey))
  );
  const selected = useMemo(
    () =>
      props.layout === 'bundle'
        ? props.targets.filter((t) => selectedKeys.has(shareTargetKey(t)))
        : props.targets,
    [props.layout, props.targets, selectedKeys]
  );
  // Access shown is the first chosen item's — the chat, unless it was unticked
  // to share only what's in it.
  const [subject] = selected;
  const allowView = canOfferViewFor(selected);
  const [linkSettings, setLinkSettings] = useState<LinkSettings>(() =>
    defaultLinkSettings(canOfferViewFor(props.targets))
  );
  const back = view === 'links' ? 'settings' : 'main';
  // "Share chat", "Share document", or "Share 3 items" for a selection.
  // Named for what is shared: 'Share "Simple Clean Agent"', the kind until
  // the name loads, or 'Share 3 items' for a selection.
  const kindTitle =
    props.layout === 'bulk'
      ? `Share ${props.targets.length} items`
      : `Share ${RESOURCE_TYPE_CONFIG[props.first.type].label.toLowerCase()}`;
  const firstName = useShareTargetTitle(props.first).trim();
  // "Untitled …" is also what shows while the name loads; the kind reads better.
  const title =
    props.layout === 'bulk' ||
    firstName === '' ||
    firstName.startsWith('Untitled ')
      ? kindTitle
      : `Share “${firstName}”`;
  // A chat with things made in it gets an "Includes" row to leave some
  // of them out.
  const [maybeChat, ...rest] = props.targets;
  const chat =
    props.layout === 'bundle' &&
    maybeChat?.type === ResourceType.Chat &&
    rest.length > 0
      ? maybeChat
      : null;

  const body =
    subject === undefined ? (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <ListChecks className="h-5 w-5" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">Nothing selected</p>
          <p className="max-w-[16rem] text-sm text-muted-foreground">
            Tick at least one item under Includes.
          </p>
        </div>
      </div>
    ) : view === 'main' || view === 'settings' ? (
      // Not keyed by subject or page: unticking the chat changes whose access
      // is shown, and a remount would flash a spinner, refocus the field and
      // drop the status line on the way back from settings.
      <ShareMain
        page={view}
        fromChat={props.targets.some((t) => t.type === ResourceType.Chat)}
        targets={selected}
        subject={subject}
        allowView={allowView}
        linkSettings={linkSettings}
        onLinkSettingsChange={setLinkSettings}
        showActiveLinks={props.layout !== 'bulk'}
        onClose={props.onClose}
        onOpenSettings={() => setView('settings')}
        onOpenActiveLinks={() => setView('links')}
      />
    ) : (
      // Padded so a focused field's ring is never clipped by the scroll edge.
      <div className="-m-1 min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-1">
        <ShareActiveLinks target={subject} />
      </div>
    );

  const controls = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col px-6 pb-5 pt-5">
      <header className="flex shrink-0 items-center gap-1 pb-4">
        {view === 'main' ? null : (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Back"
            className="-ml-1.5"
            onClick={() => setView(back)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <h2 className="min-w-0 flex-1 truncate text-base font-medium">
          {view === 'main'
            ? title
            : view === 'settings'
            ? `${kindTitle} settings`
            : 'Active links'}
        </h2>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          className="-mr-1.5 text-muted-foreground"
          onClick={props.onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </header>
      {chat !== null && view === 'main' ? (
        <div className="shrink-0 pb-5">
          <ShareWhatTheyGet
            chat={chat}
            items={rest}
            selectedKeys={selectedKeys}
            onChange={setSelectedKeys}
          />
        </div>
      ) : null}
      {body}
    </div>
  );

  // Focusable (but not tabbable) so a click on a row label or empty space
  // lands focus here. Otherwise that click drops focus to the page, and the
  // modal's focus trap pulls it back to the last field it saw.
  return (
    <div
      tabIndex={-1}
      className={cn(
        'coco-warm-surfaces flex w-[min(36rem,94vw)] flex-col outline-none',
        // The "Includes" row needs a little more height.
        chat === null ? 'h-[min(34rem,85dvh)]' : 'h-[min(38rem,85dvh)]'
      )}
    >
      {controls}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The main view: invite, who has access, and a link
// ---------------------------------------------------------------------------

/** What the last action did, shown above the footer. */
type Status = { text: string; tone?: 'warning' };

/**
 * The three ways to share, each with its own control and none behind a tab,
 * the way Claude's share dialog lays them out: the field and its Add
 * button for people, the general access row for the whole org, and Copy link
 * in the footer. Each says what it did in the status line.
 *
 * With several items the list is the merged truth across all of them, and
 * every change goes to each item the viewer is allowed to manage.
 */
const ShareMain: React.FC<{
  page: 'main' | 'settings';
  /**
   * Shared from a chat, whose Includes checklist already says what goes
   * with it.
   */
  fromChat: boolean;
  targets: ShareTarget[];
  subject: ShareTarget;
  allowView: boolean;
  linkSettings: LinkSettings;
  onLinkSettingsChange: (next: LinkSettings) => void;
  /** Off in bulk, where "the links on this" has no single subject. */
  showActiveLinks: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  onOpenActiveLinks: () => void;
}> = ({
  page,
  fromChat,
  targets,
  subject,
  allowView,
  linkSettings,
  onLinkSettingsChange,
  showActiveLinks,
  onClose,
  onOpenSettings,
  onOpenActiveLinks,
}) => {
  const combined = useCombinedAccess(targets);
  const access = combined.access;
  const manageable = access?.manageable ?? [];
  const canManage = manageable.length > 0;
  const unmanageableCount =
    access === null ? 0 : access.total - manageable.length;

  const client = useTenantedClient();
  const orgName = useWhoami((s) => s.org.name);
  const orgSlug = useWhoami((s) => s.org.slug);
  const toolsWarning = useShareToolsWarning({
    client,
    resolvedTargets: targets,
  });
  const { apply, applyTo, applyOnce, pending } =
    useApplyShareChange(manageable);
  // Leaving is about the person's own access, not what they can manage: a
  // viewer who can't manage anything must still be able to remove themselves,
  // so self-removal runs over every target, not just the manageable ones.
  const { apply: applyAsSelf } = useApplyShareChange(targets);
  const link = useCopyShareLink(manageable, allowView);

  const [staged, setStaged] = useState<Invitee[]>([]);
  const [invitePermission, setInvitePermission] =
    useState<SharePermission>('edit');
  // Off by default: adding someone gives access, and an email is extra.
  const [notify, setNotify] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  // Links for outside invites that weren't emailed, handed over to send by hand.
  const [handoff, setHandoff] = useState<
    ReadonlyArray<{ email: string; url: string }>
  >([]);

  // An outside address only gets in through the link Coco emails, so default
  // Notify on once one is staged. Someone already in the org needs no email, so
  // this only turns Notify on, never off, leaving a deliberate uncheck alone.
  const hasStagedEmail = staged.some((i) => i.kind === 'email');
  useEffect(() => {
    if (hasStagedEmail) {
      setNotify(true);
    }
  }, [hasStagedEmail]);

  // A confirmation clears itself the way a toast would; a warning stays until
  // the next action, since it reports something that needs reading.
  useEffect(() => {
    if (status === null || status.tone === 'warning') {
      return;
    }
    const timer = setTimeout(() => setStatus(null), 4500);
    return () => clearTimeout(timer);
  }, [status]);
  const [confirmingUnshare, setConfirmingUnshare] = useState(false);
  const [confirmingLimit, setConfirmingLimit] = useState(false);

  const org = access?.org ?? null;
  const total = access?.total ?? targets.length;
  const inheritedOrgFolder =
    access?.inherited.find((folder) => folder.orgPermission !== null) ?? null;
  // Items in a folder the viewer can limit to the people added to them, and
  // items already limited. A folder always follows the one it's in.
  const manageableKeys = new Set(manageable.map(shareTargetKey));
  const limitable = (access?.inFolders ?? []).filter(
    (item) =>
      item.target.type !== ResourceType.Folder &&
      manageableKeys.has(shareTargetKey(item.target))
  );
  const followingFolder = limitable.filter(
    (item) => item.folder.followsSharing
  );
  const limitedInFolder = limitable.filter(
    (item) => !item.folder.followsSharing
  );
  // Folder sharing that reaches these and could be cut off here.
  const folderLetsPeopleIn =
    followingFolder.length > 0 && (access?.inherited.length ?? 0) > 0;
  // Docs' default: a whole org gets to read, and editing is a step up.
  const defaultOrgPermission: SharePermission = allowView ? 'view' : 'edit';
  const many = total > 1;
  const what = many ? `these ${total} items` : 'it';

  // Shared with the whole org on everything, at edit or at the link's level.
  const orgOnAll = org !== null && org.onCount === total;
  const orgCanEdit = orgOnAll && org.permission !== 'view';
  // One page to point at: a single item, or a chat, whose page opens what's in
  // it. Several unrelated items need a link that covers them all.
  const onePageOpensAll = total === 1 || fromChat;
  const orgCoversLink =
    onePageOpensAll &&
    linkSettings.mode === 'access' &&
    orgOnAll &&
    (orgCanEdit || effectivePermission(linkSettings, allowView) === 'view');

  const generalAccess: GeneralAccess =
    org !== null
      ? {
          kind: 'org',
          permission: toSharePermission(org.permission),
          onCount: org.onCount,
        }
      : inheritedOrgFolder !== null
      ? {
          kind: 'folder',
          permission: toSharePermission(inheritedOrgFolder.orgPermission),
          folderName: inheritedOrgFolder.folderName,
        }
      : {
          kind: 'invited',
          limitedIn: limitedInFolder[0]?.folder.folderName ?? null,
        };

  /** Share with the org at `permission`, then say `done` in the status line. */
  const shareWithOrg = async (permission: SharePermission, done: string) => {
    const values = await apply(
      org === null ? 'org_shared' : 'org_changed',
      (t) =>
        client.resource.setSharedWithOrganization({
          targetResourceType: t.type,
          targetResourceId: t.id,
          permission,
        })
    );
    if (values !== null) {
      setStatus({ text: done });
    }
  };

  const changeOrgLevel = (permission: SharePermission) =>
    void shareWithOrg(
      permission,
      `Everyone at ${orgName} can ${permission} now.`
    );

  /** Stop sharing with the org, asking first if that would lock you out. */
  const requestUnshare = () => {
    if (access !== null && !access.viewer.hasAccessWithoutOrgShare) {
      setConfirmingUnshare(true);
      return;
    }
    void unshareOrg();
  };

  const unshareOrg = async () => {
    setConfirmingUnshare(false);
    const values = await apply('org_unshared', (t) =>
      client.resource.removeSharedWithOrganization({ type: t.type, id: t.id })
    );
    if (values !== null) {
      setStatus({
        // A folder shared with the org still lets everyone in.
        text:
          inheritedOrgFolder !== null
            ? `Stopped sharing with ${orgName} directly. Everyone at ${orgName} can still open ${what} through the folder “${inheritedOrgFolder.folderName}”.`
            : `Stopped sharing with ${orgName}. Only people added can open ${what} now.`,
      });
    }
  };

  /**
   * Limit to the people added: stop the org share, if any, and cut the
   * folder's sharing off. Each item stays in its folder.
   */
  const limitToPeopleAdded = async () => {
    setConfirmingLimit(false);
    if (org !== null) {
      const unshared = await apply('org_unshared', (t) =>
        client.resource.removeSharedWithOrganization({ type: t.type, id: t.id })
      );
      if (unshared === null) {
        return;
      }
    }
    const values = await applyTo(
      followingFolder.map((item) => item.target),
      'folder_sharing_limited',
      (t) =>
        client.resource.setFollowsFolderSharing({
          type: t.type,
          id: t.id,
          follows: false,
        })
    );
    if (values !== null) {
      const folderName = followingFolder[0]?.folder.folderName ?? '';
      setStatus({
        text: `Limited to people added. ${
          many ? 'They stay' : 'It stays'
        } in “${folderName}”, but its sharing doesn't reach ${
          many ? 'them' : 'it'
        } anymore.`,
      });
    }
  };

  const followFolderAgain = async () => {
    const values = await applyTo(
      limitedInFolder.map((item) => item.target),
      'folder_sharing_followed',
      (t) =>
        client.resource.setFollowsFolderSharing({
          type: t.type,
          id: t.id,
          follows: true,
        })
    );
    if (values !== null) {
      setStatus({
        text: `Follows the sharing of “${
          limitedInFolder[0]?.folder.folderName ?? 'its folder'
        }” again.`,
      });
    }
  };

  const invite = async () => {
    const members = staged.flatMap((i) =>
      i.kind === 'member' ? [i.member] : []
    );
    const emails = staged.flatMap((i) => (i.kind === 'email' ? [i.email] : []));
    if (members.length === 0 && emails.length === 0) {
      return;
    }
    // If one invite fails, whoever was already invited leaves the field, so
    // trying again doesn't invite (or email) them twice.
    const done = new Set<string>();
    const stopAt = () =>
      setStaged(staged.filter((i) => !done.has(inviteeKey(i))));

    let emailed = false;
    for (const member of members) {
      const values = await apply('person_added', (t, index) =>
        client.resource.setUserPermission({
          type: t.type,
          id: t.id,
          userId: member.id,
          permission: invitePermission,
          // One email per person, about the first item and the rest in all.
          ...(notify &&
            index === 0 && {
              notify: { alsoSharing: manageable.length - 1 },
            }),
        })
      );
      if (values === null) {
        stopAt();
        return;
      }
      done.add(inviteeKey({ kind: 'member', member }));
      emailed = emailed || values[0]?.notified === true;
    }

    // Someone outside the org gets in through a link that only opens for
    // their address. With Notify on, Coco emails it; otherwise, or if the
    // email didn't go out, it is handed over below to send by hand.
    const sentTo: string[] = [];
    const unsent: Array<{ email: string; url: string }> = [];
    for (const email of emails) {
      const sent = await applyOnce('outside_person_invited', () =>
        client.resource.inviteByEmail({
          targets: manageable.map((t) => ({ type: t.type, id: t.id })),
          email,
          permission: invitePermission,
          notify,
        })
      );
      if (sent === null) {
        stopAt();
        return;
      }
      done.add(inviteeKey({ kind: 'email', email }));
      if (sent.notified) {
        sentTo.push(email);
      } else {
        unsent.push({ email, url: buildShareUrl(sent.url, sent.token) });
      }
    }
    const [onlyUnsent] = unsent;
    const parts: string[] = [];
    const [onlyMember] = members;
    if (members.length > 0) {
      parts.push(
        members.length === 1 && onlyMember !== undefined
          ? `Added ${onlyMember.name ?? onlyMember.email}.`
          : `Added ${members.length} people.`
      );
      parts.push(
        emailed
          ? `We emailed ${
              members.length === 1 ? 'them' : 'each of them'
            } a link.`
          : 'They can find it under Shared with me.'
      );
    }
    if (sentTo.length > 0) {
      parts.push(
        sentTo.length === 1
          ? `Added ${sentTo[0]}. We emailed them a link.`
          : `Added ${sentTo.length} people from outside ${orgName}. We emailed each of them a link.`
      );
    }
    // A link that wasn't emailed is the only way in for that address, and its
    // token is shown once, so hand it over as a copyable row that stays put
    // rather than a message that clears itself.
    setHandoff(unsent);
    if (onlyUnsent !== undefined) {
      const who =
        unsent.length === 1 ? onlyUnsent.email : `${unsent.length} people`;
      parts.push(notify ? `Couldn't email ${who}.` : `Added ${who}.`);
    }
    setStatus({
      text: parts.join(' '),
      // Only a failed email is a warning; a quiet add is what was asked for.
      ...(notify && unsent.length > 0 && { tone: 'warning' as const }),
    });
    setStaged([]);
  };

  const copyHandoff = async (url: string) => {
    const ok = await navigator.clipboard.writeText(url).then(
      () => true,
      () => false
    );
    if (ok) {
      toast.success('Link copied');
    } else {
      toast.error("Couldn't copy the link. Select it and copy it by hand.");
    }
  };

  const copyLink = async () => {
    // Everyone here can already open it at least as well as a link would let
    // them, so the page address does the job without minting a link.
    if (orgCoversLink) {
      await link.copyAddress(pageAddress(orgSlug, subject));
      setStatus({
        text: `Link copied. Everyone at ${orgName} can open it.`,
      });
      return;
    }
    const url = await link.copy(linkSettings);
    if (url === null) {
      return;
    }
    setStatus({
      text:
        linkSettings.mode === 'copy'
          ? 'Link copied. Each person who opens it gets their own copy.'
          : `Link copied. ${linkAudienceLabel(
              linkSettings,
              orgName
            )} with it can ${
              effectivePermission(linkSettings, allowView) === 'view'
                ? 'view'
                : 'edit'
            }.`,
    });
  };

  if (combined.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-10">
        <Loading.Spinner variant="sm" />
      </div>
    );
  }
  if (access === null) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load who has access</AlertTitle>
        <AlertDescription>
          {combined.error?.message ?? 'Try again in a moment.'}
        </AlertDescription>
      </Alert>
    );
  }

  const handoffSection =
    handoff.length > 0 ? (
      <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
        <div className="mb-2 flex items-start gap-2">
          <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="flex-1 text-xs text-muted-foreground">
            {handoff.length === 1
              ? `Send ${
                  handoff[0]?.email ?? 'them'
                } this link so they can open it. It won't be shown again.`
              : `Send each of these ${handoff.length} people their link so they can open it. They won't be shown again.`}
          </p>
          <button
            type="button"
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setHandoff([])}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="flex flex-col gap-2">
          {handoff.map((h) => (
            <li key={h.email} className="flex items-center gap-2">
              <input
                readOnly
                value={h.url}
                aria-label={`Invite link for ${h.email}`}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                aria-label={`Copy invite link for ${h.email}`}
                onClick={() => void copyHandoff(h.url)}
              >
                Copy link
              </Button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const statusLine =
    status !== null ? (
      <p
        role="status"
        className={cn(
          'mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm',
          status.tone === 'warning'
            ? 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
            : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
        )}
      >
        {status.tone === 'warning' ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        {/* A link handed over in the text is one long word; let it wrap. */}
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {status.text}
        </span>
      </p>
    ) : null;

  const unshareConfirm = confirmingUnshare ? (
    <ShareConfirmAlert
      title="You'll lose access too"
      confirmLabel="Stop sharing"
      onConfirm={() => void unshareOrg()}
      onCancel={() => setConfirmingUnshare(false)}
    >
      You can open this because it's shared with {orgName}. Stopping removes
      your access as well, unless the owner adds you.
    </ShareConfirmAlert>
  ) : null;

  const limitConfirm = confirmingLimit ? (
    <ShareConfirmAlert
      title="Limit to people added?"
      confirmLabel="Limit access"
      onConfirm={() => void limitToPeopleAdded()}
      onCancel={() => setConfirmingLimit(false)}
    >
      {many ? 'They stay' : 'It stays'} in the folder “
      {followingFolder[0]?.folder.folderName}”, but the folder's sharing won't
      reach {many ? 'them' : 'it'} anymore.{' '}
      {inheritedOrgFolder !== null ? `Everyone at ${orgName}` : 'People'} who
      get in through the folder lose access unless they're added here. You keep
      yours.
    </ShareConfirmAlert>
  ) : null;

  const linkedNote =
    access.linked.length > 0 && !fromChat ? (
      <LinkedNote
        items={access.linked}
        usesItems={targets.some((t) => t.type === ResourceType.Folder)}
      />
    ) : null;

  const toolsNote =
    toolsWarning.status === 'ready' && toolsWarning.hasConfiguredCredentials ? (
      <Note>
        {toolsWarning.affectedToolCount === 1
          ? "1 tool in what you're sharing runs"
          : `${toolsWarning.affectedToolCount} tools in what you're sharing run`}{' '}
        on your connected credentials. People with access can trigger{' '}
        {toolsWarning.affectedToolCount === 1 ? 'it' : 'them'}.
      </Note>
    ) : null;

  // Share settings, as in Claude's: who has access on top, the link below.
  if (page === 'settings' && canManage) {
    const orgOn = org !== null;
    // Open to the org only through a folder: the same "Everyone at" as the
    // main view, but the level and the way back to private live on the folder.
    const orgThroughFolder = org === null ? inheritedOrgFolder : null;
    const folderLevel: SharePermission = toSharePermission(
      orgThroughFolder?.orgPermission
    );
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="-m-1 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden p-1">
          <section className="flex flex-col gap-2">
            <SectionLabel>Who has access</SectionLabel>
            <div className="flex items-center gap-2">
              <Select
                value={orgOn || orgThroughFolder !== null ? 'org' : 'invited'}
                disabled={pending}
                onValueChange={(next) => {
                  if (next === 'org' && !orgOn) {
                    void shareWithOrg(
                      defaultOrgPermission,
                      `Shared with everyone at ${orgName}. They can ${defaultOrgPermission}.`
                    );
                  } else if (next === 'invited' && folderLetsPeopleIn) {
                    setConfirmingLimit(true);
                  } else if (next === 'invited' && orgOn) {
                    requestUnshare();
                  }
                }}
              >
                <SelectTrigger
                  aria-label="Who has access"
                  className="h-10 min-w-0 flex-1 rounded-lg border-none bg-muted px-3 text-sm shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl p-1">
                  <SelectItem
                    value="invited"
                    disabled={orgThroughFolder !== null && !folderLetsPeopleIn}
                    className="h-9 rounded-lg"
                  >
                    <Lock className="h-4 w-4" />
                    Only people added
                  </SelectItem>
                  <SelectItem value="org" className="h-9 rounded-lg">
                    <Building2 className="h-4 w-4" />
                    Everyone at {orgName}
                  </SelectItem>
                </SelectContent>
              </Select>
              {orgThroughFolder !== null ? (
                <Tooltip
                  side="bottom"
                  tooltipContent="Set by the folder's sharing."
                >
                  <Pill>{permissionLabel(folderLevel)}</Pill>
                </Tooltip>
              ) : org === null ? null : (
                <SharePermissionSelect
                  value={toSharePermission(org.permission)}
                  onChange={changeOrgLevel}
                  allowView={allowView}
                  disabled={pending}
                  aria-label={`Access for everyone at ${orgName}`}
                  className="h-10 rounded-lg border border-solid border-input px-3 text-sm"
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {orgOn
                ? `Anyone at ${orgName} can find and open ${what}.`
                : orgThroughFolder !== null
                ? `Anyone at ${orgName} can open ${what} through the folder “${
                    orgThroughFolder.folderName
                  }”.${
                    folderLetsPeopleIn
                      ? ` Choose Only people added to limit ${
                          many ? 'them' : 'it'
                        } to the people added here; ${
                          many ? 'they stay' : 'it stays'
                        } in the folder.`
                      : " Change this in the folder's sharing."
                  }`
                : 'Only people you add, or send a link to, can open it.'}
            </p>
            {limitedInFolder.length > 0 ? (
              <p className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                {limitedInFolder.length === 1
                  ? `In the folder “${limitedInFolder[0]?.folder.folderName}”, but it doesn't follow the folder's sharing.`
                  : `${limitedInFolder.length} of these are in a folder but don't follow its sharing.`}
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => void followFolderAgain()}
                  className="font-medium text-primary hover:underline"
                >
                  Follow the folder's sharing
                </button>
              </p>
            ) : null}
            {org !== null && org.onCount < total ? (
              <p className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                Shared with {orgName} on {org.onCount} of {total} items.
                <button
                  type="button"
                  disabled={pending}
                  onClick={() =>
                    void shareWithOrg(
                      toSharePermission(org.permission),
                      `Shared all ${total} items with everyone at ${orgName}.`
                    )
                  }
                  className="font-medium text-primary hover:underline"
                >
                  Share all {total}
                </button>
              </p>
            ) : null}
            {orgThroughFolder !== null ? (
              <p className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                To keep it open if it moves out,
                <button
                  type="button"
                  disabled={pending}
                  onClick={() =>
                    void shareWithOrg(
                      folderLevel,
                      `Shared with everyone at ${orgName} directly.`
                    )
                  }
                  className="font-medium text-primary hover:underline"
                >
                  share it with {orgName} directly
                </button>
              </p>
            ) : null}
            {unshareConfirm}
            {limitConfirm}
          </section>

          {linkedNote}
          {toolsNote}

          <section className="flex flex-col gap-3 border-t border-border pt-5">
            <SectionLabel>Link</SectionLabel>
            <ShareSettingsView
              settings={linkSettings}
              onChange={onLinkSettingsChange}
              allowView={allowView}
              subject={subject}
              showActiveLinks={showActiveLinks}
              onOpenActiveLinks={onOpenActiveLinks}
              orgCanEdit={orgCanEdit && onePageOpensAll}
            />
          </section>
        </div>
        {statusLine}
      </div>
    );
  }

  const peopleList = (
    <ul className="-my-1 flex flex-col">
      <GeneralAccessRow
        state={generalAccess}
        orgName={orgName}
        total={total}
        peopleCount={access.people.length + access.pendingInvites.length}
        onOpen={canManage ? onOpenSettings : undefined}
        trailing={
          // Shared with the org directly: change or stop it right here, the
          // way a person's row works.
          canManage && org !== null ? (
            <SharePermissionSelect
              value={toSharePermission(org.permission)}
              onChange={changeOrgLevel}
              onRemove={requestUnshare}
              removeLabel={`Stop sharing with ${orgName}`}
              allowView={allowView}
              disabled={pending}
              aria-label={`Access for everyone at ${orgName}`}
            />
          ) : undefined
        }
      />
      <PeopleWithAccess
        access={access}
        onRemoveInvite={async (invite) => {
          const done = await applyOnce('invite_removed', () =>
            client.resource.revokeShareLink({ shareLinkId: invite.linkId })
          );
          if (done !== null) {
            setStatus({ text: `Removed ${invite.email}.` });
          }
        }}
        onRemoveOrg={async (externalOrg) => {
          const values = await apply('external_org_removed', (t) =>
            client.resource.removeSharedWithOrganization({
              type: t.type,
              id: t.id,
              granteeOrgId: externalOrg.orgId,
            })
          );
          if (values !== null) {
            setStatus({
              text: `Stopped sharing with ${
                externalOrg.name ?? 'that organization'
              }.`,
            });
          }
        }}
        canManage={canManage}
        allowView={allowView}
        disabled={pending}
        onChange={async (userId, permission) => {
          const values = await apply('person_changed', (t) =>
            client.resource.setUserPermission({
              type: t.type,
              id: t.id,
              userId,
              permission,
            })
          );
          if (values !== null) {
            setStatus({ text: 'Access updated.' });
          }
        }}
        onRemove={async (userId, isSelf) => {
          const values = await (isSelf ? applyAsSelf : apply)(
            'person_removed',
            (t) =>
              client.resource.removeUserPermission({
                type: t.type,
                id: t.id,
                userId,
              }),
            isSelf ? `You left ${describeShareTargets(targets)}` : undefined
          );
          if (values !== null && isSelf) {
            onClose();
          } else if (values !== null) {
            setStatus({ text: 'Access removed.' });
          }
        }}
      />
    </ul>
  );

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="-m-1 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden p-1">
          <SectionLabel>Who has access</SectionLabel>
          {peopleList}
        </div>
        <ViewerFooter subject={subject} access={access} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Padded so a focused field's ring is never clipped by the scroll edge. */}
      <div className="-m-1 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden p-1">
        <InvitePeople
          access={access}
          staged={staged}
          onStagedChange={setStaged}
          permission={invitePermission}
          onPermissionChange={setInvitePermission}
          allowView={allowView}
          disabled={pending}
          onSubmit={() => void invite()}
          action={
            <Button
              variant="outline"
              className="h-11 px-4"
              loading={pending && staged.length > 0}
              disabled={pending || staged.length === 0}
              onClick={() => void invite()}
            >
              Add
            </Button>
          }
          footer={
            <label className="flex w-fit cursor-pointer items-center gap-2 px-0.5 pt-1.5 text-xs text-muted-foreground">
              <Checkbox
                checked={notify}
                onCheckedChange={(next) => setNotify(next === true)}
                aria-label="Notify people by email"
              />
              Notify people by email
            </label>
          }
        />

        {unmanageableCount > 0 ? (
          <Note>
            You can't change sharing on {unmanageableCount} of these{' '}
            {unmanageableCount === 1 ? 'item' : 'items'}; changes apply to the
            rest.
          </Note>
        ) : null}
        {linkedNote}
        {toolsNote}

        <div className="flex flex-col gap-2">
          <SectionLabel>Who has access</SectionLabel>
          {peopleList}
        </div>
        {unshareConfirm}
      </div>

      {statusLine}
      {handoffSection}

      <footer className="mt-3 flex shrink-0 items-center gap-2 border-t border-border pt-4">
        <span className="flex-1" />
        <Tooltip side="top" tooltipContent="Link settings">
          <Button
            variant="outline"
            size="icon"
            aria-label="Link settings"
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Button
          loading={link.creating}
          disabled={link.creating}
          onClick={() => void copyLink()}
        >
          {link.copied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          {link.copied ? 'Link copied' : 'Copy link'}
        </Button>
      </footer>
    </div>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="text-xs font-medium text-muted-foreground">{children}</p>;

/**
 * Says that what the shared thing uses comes with it, whichever way it is
 * shared, so an agent's documents or a workflow's datasets open for them too.
 * A count, not a list: an agent can use a lot.
 */
const LinkedNote: React.FC<{
  items: CombinedAccess['linked'];
  /** A folder shares what its items use, not what it uses; word it that way. */
  usesItems: boolean;
}> = ({ items, usesItems }) => (
  <Note>
    {usesItems
      ? `Also shares ${
          items.length === 1 ? '1 more thing' : `${items.length} more things`
        } the items in it use.`
      : `Also shares ${
          items.length === 1 ? '1 thing' : `the ${items.length} things`
        } it uses.`}
  </Note>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="flex gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>{children}</span>
  </p>
);

// ---------------------------------------------------------------------------
// For people who can see access but not change it
// ---------------------------------------------------------------------------

/**
 * The link a viewer can copy is the plain page address — it works for anyone
 * already listed above, which is all a viewer can promise.
 */
const ViewerFooter: React.FC<{
  subject: ShareTarget;
  access: CombinedAccess;
}> = ({ subject, access }) => {
  const orgSlug = useWhoami((s) => s.org.slug);
  const ownerName = access.owner.name ?? access.owner.email ?? 'the owner';
  const copyPageLink = async () => {
    await navigator.clipboard.writeText(pageAddress(orgSlug, subject));
    toast.success('Link copied. It works for people who already have access.');
  };
  return (
    <footer className="mt-3 flex shrink-0 items-center gap-3 border-t border-border pt-4">
      <p className="flex-1 text-xs text-muted-foreground">
        Only editors can add people. Ask {ownerName}.
      </p>
      <Button variant="outline" onClick={() => void copyPageLink()}>
        <Link2 className="h-4 w-4" />
        Copy link
      </Button>
    </footer>
  );
};

/** The address of a resource's own page, for people who can already open it. */
const pageAddress = (orgSlug: string, target: ShareTarget): string =>
  `${window.location.origin}${resourceItemPath(
    orgSlug,
    target.type,
    target.id
  )}`;
